import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { authClient } from "@/lib/auth-client";
import { useState, useRef, useEffect } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useOnboarding } from "@/hooks/useOnboarding";
import FormShell from "@/components/form-shell";
import { useRightPanel } from "@/hooks/useRightPanel";
import { zodResolver } from "@hookform/resolvers/zod";
import { onboardingStep1EmailSchema, onboardingStep1FullSchema, type OnboardingStep1FormValues } from "@/schemas/onboarding-schema";
import { api } from "@api";

// Composant pour le message d'erreur avec lien
const EmailExistsError = ({ email }: { email: string }) => (
  <div className="text-center text-sm font-medium text-destructive">
    Un compte existe déjà avec cet email. Veuillez utiliser un autre email ou{" "}
    <Link to={`/login?email=${encodeURIComponent(email)}`} className="font-medium text-primary underline">
      vous connecter.
    </Link>
  </div>
);

export function OnboardingStep1() {
  const [searchParams, setSearchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [lastCheckedEmail, setLastCheckedEmail] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setRight } = useRightPanel();

  // Use the hook without email to check initially
  useOnboarding("step1");

  // Mutation pour vérifier la disponibilité de l'email
  const checkEmailMutation = api.useMutation("get", "/onboarding/check-email/{email}");

  useEffect(() => {
    setRight(<div className="flex h-full flex-1 items-center justify-center overflow-hidden p-6 text-6xl font-bold text-black">Nautika</div>);
  }, [setRight]);

  // Formulaire pour l'étape 0 (email seulement)
  const emailForm = useForm<{ email: string }>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(onboardingStep1EmailSchema),
    defaultValues: { email: emailParam },
    criteriaMode: "firstError",
  });

  // Formulaire pour l'étape 1 (mot de passe + confirmation)
  const passwordForm = useForm<OnboardingStep1FormValues>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(onboardingStep1FullSchema),
    defaultValues: { email: "", password: "", confirmPassword: "" },
    criteriaMode: "firstError",
  });

  // Remove email query param after initialization
  useEffect(() => {
    if (emailParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("email");
      setSearchParams(newParams, { replace: true });
    }
  }, [emailParam, searchParams, setSearchParams]);

  // Clear errors when switching steps
  useEffect(() => {
    if (step === 0) {
      emailForm.clearErrors();
    } else {
      passwordForm.clearErrors();
    }
  }, [step, emailForm, passwordForm]);

  // Step 0: email validation
  const handleEmailNext = async (data: { email: string }) => {
    setIsLoading(true);
    const ok = await emailForm.trigger("email");
    if (!ok) {
      setIsLoading(false);
      return;
    }

    try {
      // Use the mutation to check email availability
      const result = await checkEmailMutation.mutateAsync({
        params: {
          path: { email: data.email },
        },
      });

      if (result?.status === "not_available" || result?.status === "onboarding") {
        emailForm.setError("email", { type: "manual", message: " " });
        setLastCheckedEmail(data.email);
        setIsLoading(false);
        return;
      }

      // Set email in password form and switch to step 1
      passwordForm.setValue("email", data.email);
      setStep(1);
      setTimeout(() => {
        document.getElementById("password")?.focus();
      }, 1);
    } catch (error) {
      console.error("Email availability check failed:", error);
      toast.error("Erreur lors de la vérification de l'email");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: passwords and signup
  const handlePasswordsSubmit = async (data: OnboardingStep1FormValues) => {
    setIsLoading(true);
    // Trigger validation for the entire form to ensure refinement validation runs
    const ok = await passwordForm.trigger();
    if (!ok) {
      setIsLoading(false);
      return;
    }
    await authClient.signUp.email(
      { email: data.email, password: data.password, name: "", first_name: "", onboarding: "step2" },
      {
        onError: (error) => {
          toast.error("Erreur lors de la création du compte. Veuillez réessayer.", { description: error.error.statusText });
          setIsLoading(false);
        },
        onSuccess: () => {
          toast.success("Compte créé avec succès !");
          navigate("/onboarding/step2", { replace: true });
          setIsLoading(false);
        },
      }
    );
  };

  const goBackToEmail = () => {
    setStep(0);
    setTimeout(() => emailInputRef.current?.focus(), 0);
  };

  return (
    <FormShell
      title="Créer votre compte"
      subtitle={
        <>
          Vous avez déjà un compte ?{" "}
          <Link to="/login" className="underline underline-offset-4 hover:text-primary">
            Connectez-vous
          </Link>
        </>
      }
      onSubmit={step === 0 ? emailForm.handleSubmit(handleEmailNext) : passwordForm.handleSubmit(handlePasswordsSubmit)}
      footer={
        <div className="text-center text-sm text-muted-foreground">
          En vous inscrivant, vous acceptez nos{" "}
          <Link to="/terms" className="font-medium underline hover:text-primary">
            Conditions Générales d'Utilisation
          </Link>{" "}
          et notre{" "}
          <Link to="/privacy" className="font-medium underline hover:text-primary">
            Politique de Confidentialité
          </Link>
          .
        </div>
      }
    >
      {step === 0 ? (
        <Form {...emailForm}>
          <div className="grid gap-6">
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <FormItem className="grid gap-3">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="mail@example.com" {...field} ref={emailInputRef} autoComplete="email" />
                  </FormControl>
                  <FormMessage />
                  {emailForm.formState.errors.email && lastCheckedEmail && <EmailExistsError email={lastCheckedEmail} />}
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vérification...
                </>
              ) : (
                "Continuer"
              )}
            </Button>
          </div>
        </Form>
      ) : (
        <Form {...passwordForm}>
          <div className="grid gap-6">
            <div className="flex items-end gap-4">
              <FormField
                control={passwordForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="grid flex-1 gap-3">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="mail@example.com" {...field} disabled autoComplete="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="button" variant="outline" className="mb-0.5" onClick={goBackToEmail}>
                Modifier
              </Button>
            </div>

            <FormField
              control={passwordForm.control}
              name="password"
              render={({ field }) => (
                <FormItem className="grid gap-3">
                  <div className="flex items-center justify-between">
                    <FormLabel>Mot de passe</FormLabel>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} {...field} id="password" autoComplete="new-password" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        tabIndex={-1}
                        className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={passwordForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="grid gap-3">
                  <FormLabel>Confirmer le mot de passe</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input type={showConfirmPassword ? "text" : "password"} {...field} id="confirmPassword" autoComplete="new-password" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        tabIndex={-1}
                        className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Inscription...
                </>
              ) : (
                "Créer mon compte"
              )}
            </Button>
          </div>
        </Form>
      )}
    </FormShell>
  );
}
